


















































import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import MStoreFormSelect from '@/components/shared/MStoreFormSelect.vue';
import DocumentViewer from '@/components/shared/DocumentViewer.vue';
import ErrorNotification from '@/components/shared/ErrorNotification.vue';
import {
  BaseDocumentSearchModel,
  Cabinet,
  CabinetModel,
  ColumnsModel,
  Dictionary,
  DocumentReferencesModel,
  DocumentReferencesResultModel,
  DocumentToStoreComponents,
  ErrorModel,
  ExistingDocumentOption,
  ReferenceLookupSearchModel,
  SchemaModel,
  DocumentReferenceModel,
  DocumentViewerConstants,
  DocumentsToStore,
  EditReferencesComponents
} from '@/types';
import { ReferenceLookupService } from '@/services';
import { DateUtils } from '@/utils';
import { componentsPlugin } from 'bootstrap-vue';

export default Vue.extend({
  name: 'SelectExistingDocument',
  props: {
    storeNamespace: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedExistingDocumentOption: ExistingDocumentOption.AddToDocument,
      selectedExistingDocumentId: 0,
      documentReferencesResult: {} as DocumentReferencesResultModel,
      isLoading: false,
      documentViewerToolOptions: DocumentViewerConstants.DocumentViewerDeletePageToolOptions
    };
  },
  methods: {
    ...mapActions('applicationDirtyStore', ['setApplicationDirty']),
    ...mapActions({
      setComponentName(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setComponentName`, payload);
      },
      setExistingDocumentId(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setExistingDocumentId`, payload);
      },
      setExistingDocumentOption(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setExistingDocumentOption`, payload);
      },
      setSchemaValuesFromReferences(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setSchemaValuesFromReferences`, payload);
      },
      setDocumentDateValue(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setDocumentDateValue`, payload);
      },
      setMultiValueReferencesFromExistingDocument(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setMultiValueReferencesFromExistingDocument`, payload);
      }
    }),
    ...mapActions('documentViewStore', ['loadPage', 'unloadDocument', 'setDocumentSearchModel']),
    async loadExistingDocumentsAsync() {
      try {
        this.isLoading = true;

        const references = {} as Dictionary<string>;

        Object.keys(this.referenceValues)
          .filter((x) => x !== Cabinet.DocumentDateReference.FieldKey)
          .forEach((key) => {
            if (this.referenceValues[key]) {
              references[key] = this.referenceValues[key];
            }
          });

        const multiValueReferences = this.multiValueReferenceValues
          ? this.multiValueReferenceValues.map(({ multiValueRowId, ...rest }: any) => {
              return rest;
            })
          : [];

        const model = {
          references,
          multiValueReferences
        } as ReferenceLookupSearchModel;

        this.documentReferencesResult = await ReferenceLookupService.getDocumentsByReferences(this.targetCabinetId, this.targetDocumentTypeId, model);

        if (this.documents?.length) {
          this.onDocumentSelected(this.documents[0]);
        }
      } catch (error) {
        this.$log('Error loading existing documents', error);
      } finally {
        this.isLoading = false;
      }
    },
    onDocumentSelected(document: any) {
      this.unloadDocument();

      const documentSearchModel: BaseDocumentSearchModel = {
        cabinetId: this.targetCabinetId,
        documentId: document.id,
        documentTypeId: this.targetDocumentTypeId
      };

      this.setDocumentSearchModel(documentSearchModel);
      this.loadPage(1);
      this.selectedExistingDocumentId = document.id;
    },
    returnToDocumentToStore() {
      this.setComponentName(this.$route.name === DocumentsToStore.DocumentsToStore.name ? DocumentToStoreComponents.Default : EditReferencesComponents.Default);
    },
    onCancel() {
      this.returnToDocumentToStore();
    },
    onSubmit() {
      this.setExistingDocumentId(this.selectedExistingDocumentId);
      this.setExistingDocumentOption(this.selectedExistingDocumentOption);
      this.setSchemaValuesFromReferences(this.selectedExistingDocumentReferences);
      this.setDocumentDateValue(this.selectedExistingDocument?.documentDate);
      this.setMultiValueReferencesFromExistingDocument(this.selectedExistingDocumentMultiValueReferences);
      this.setApplicationDirty(true);
      this.returnToDocumentToStore();
    },
    selectedRowClass(item: any): string | undefined {
      if (item && item.id === this.selectedExistingDocumentId) {
        return 'table-success';
      }
    },
    isTifOrPdf(fileExtension: string) {
      switch (fileExtension) {
        case 'TIF':
        case 'TIFF':
        case 'PDF':
          return true;
        default:
          return false;
      }
    }
  },
  computed: {
    ...mapState({
      documentToStore(state: any, getters: any) {
        return getters[`${this.storeNamespace}/documentToStore`];
      },
      targetCabinetId(state: any, getters: any) {
        return getters[`${this.storeNamespace}/targetCabinetId`];
      },
      targetDocumentTypeId(state: any, getters: any) {
        return getters[`${this.storeNamespace}/targetDocumentTypeId`];
      },
      referenceValues(state: any, getters: any) {
        return getters[`${this.storeNamespace}/referenceValues`];
      },
      multiValueReferenceValues(state: any, getters: any) {
        return getters[`${this.storeNamespace}/multiValueReferenceValues`];
      },
      documentToStoreView(state: any, getters: any) {
        return getters[`${this.storeNamespace}/documentToStoreView`];
      }
    }),
    documents(this: any) {
      if (!this.documentReferencesResult || !this.documentReferencesResult.documents || !this.documentReferencesResult.documents.length) {
        return [] as DocumentReferencesModel[];
      }

      return this.documentReferencesResult.documents.filter((x: DocumentReferencesModel) => x.id !== this.sourceDocumentId);
    },
    existingDocumentsFound(this: any) {
      return this.documents?.length;
    },
    cabinet(this: any) {
      if (!this.documentReferencesResult || !this.documentReferencesResult.cabinets || !this.documentReferencesResult.cabinets.length) {
        return {} as CabinetModel;
      }

      return this.documentReferencesResult.cabinets[0];
    },
    fields() {
      if (!this.documentToStoreView || !this.documentToStoreView.dynamicFormSchema || !this.documentToStoreView.dynamicFormSchema.length) {
        return [] as ColumnsModel[];
      }
      const columns = this.documentToStoreView.dynamicFormSchema
        .filter((schema: SchemaModel) => schema.id.toLowerCase() !== Cabinet.DocumentDateReference.FieldKey.toLowerCase())
        .map((schema: SchemaModel) => {
          return { key: schema.id.toLowerCase(), label: schema.label, sortable: true };
        }) as ColumnsModel[];

      const multiValueColumns = this.documentToStoreView.multiValueDynamicFormSchema
        .filter((schema: SchemaModel) => schema.id.toLowerCase() !== Cabinet.DocumentDateReference.FieldKey.toLowerCase())
        .map((schema: SchemaModel) => {
          return { key: schema.id.toLowerCase(), label: schema.label, sortable: true };
        }) as ColumnsModel[];

      const documentDateSchema = this.documentToStoreView.dynamicFormSchema.find(
        (schema: SchemaModel) => schema.id.toLowerCase() === Cabinet.DocumentDateReference.FieldKey.toLowerCase()
      ) as SchemaModel;
      return [
        { key: 'pages', label: 'Pages', sortable: true } as ColumnsModel,
        {
          key: Cabinet.DocumentDateReference.key,
          label: documentDateSchema.label,
          sortable: true,
          formatter: DateUtils.formatDateWithoutTime
        },
        ...columns,
        ...multiValueColumns,
        { key: 'id', label: 'Document Id', sortable: true } as ColumnsModel
      ];
    },
    items(this: any) {
      if (!this.documents || !this.documents.length) {
        return [];
      }

      const items = this.documents.map((doc: DocumentReferencesModel) => {
        const item = { id: doc.id, pages: doc.pages, documentDate: doc.documentDate } as any;

        doc.references.forEach((docRef) => {
          const key = docRef.cabinetReferenceModel.fieldKey.toLowerCase();
          item[key] = docRef.value;
        });

        return item;
      });

      return items;
    },
    addToDocumentEnabled(this: any) {
      const selectedDocument = this.selectedExistingDocument;
      return (
        selectedDocument &&
        this.isTifOrPdf(selectedDocument.fileExtension) &&
        selectedDocument.fileExtension.substr(0, 3) === this.documentToStore.fileExtension.substr(0, 3) &&
        !this.documentToStore.isPdfPortFolio
      );
    },
    existingDocumentOptions(this: any) {
      const options = [
        { id: ExistingDocumentOption.ReplaceDocument, name: 'Replace the matching document ' },
        { id: ExistingDocumentOption.CreateHistoricVersion, name: 'Store as a historic version of the matching document' }
      ];

      return this.addToDocumentEnabled ? [{ id: ExistingDocumentOption.AddToDocument, name: 'Append pages to the matching document' }, ...options] : options;
    },
    submitDisabled(this: any) {
      return (
        this.isLoading || !this.selectedExistingDocumentId || !this.selectedExistingDocumentOption || !this.existingDocumentOptions.some((x: any) => x.id === this.selectedExistingDocumentOption)
      );
    },
    selectedExistingDocument(): DocumentReferencesModel | null {
      return this.documents?.find((model: DocumentReferencesModel) => model.id === this.selectedExistingDocumentId);
    },
    selectedExistingDocumentReferences(): Dictionary<string> {
      const document = this.selectedExistingDocument;
      if (!document) {
        return {} as Dictionary<string>;
      }
      const referencesDictionary = {} as Dictionary<string>;
      document.references.forEach((reference: DocumentReferenceModel) => {
        referencesDictionary[reference.cabinetReferenceModel.fieldKey] = reference.value;
      });
      return referencesDictionary;
    },
    selectedExistingDocumentMultiValueReferences(): any[] {
      const document = this.selectedExistingDocument;
      if (!document || !document.multiValueReferences) {
        return [];
      }
      return document.multiValueReferences;
    },
    sourceDocumentId: {
      get(this: any) {
        return Number(this.$route.params.documentId);
      }
    }
  },
  mounted() {
    this.loadExistingDocumentsAsync();
  },
  components: {
    MStoreTable,
    MStoreFormSelect,
    DocumentViewer,
    LoadingSpinner,
    ErrorNotification
  },
  created(this: any) {
    this.documentToolbarOptions = DocumentViewerConstants.DocumentViewerDeleteDisabledToolOptions;
    this.errorModel = {
      showHeader: false,
      message: `No existing documents were found`,
      showButton: false
    } as ErrorModel;
  }
});
